import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Phase = "join" | "stack" | "name" | "fly";

const ICON_W = Math.min(320, window.innerWidth * 0.55);
const ICON_H = Math.round(ICON_W * (512 / 461));
const EDGE = Math.max(220, window.innerWidth / 2 - ICON_W / 4 - 30);

export const IntroAnimation = ({ onDone }: { onDone: () => void }) => {
  const [phase, setPhase] = useState<Phase>("join");
  const [target, setTarget] = useState({ x: 0, y: 0 });
  const finished = useRef(false);

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    sessionStorage.setItem("fosche_intro_seen", "1");
    onDone();
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const t1 = setTimeout(() => setPhase("stack"), 2700);
    const t2 = setTimeout(() => setPhase("name"), 6300);
    const t3 = setTimeout(() => {
      const rect = document.querySelector('[data-testid="nav-logo"]')?.getBoundingClientRect();
      setTarget(
        rect
          ? {
              x: rect.left + rect.width / 2 - window.innerWidth / 2,
              y: rect.top + rect.height / 2 - window.innerHeight / 2,
            }
          : { x: -window.innerWidth / 2 + 130, y: -window.innerHeight / 2 + 45 }
      );
      setPhase("fly");
    }, 8000);
    const t4 = setTimeout(finish, 9400);
    return () => {
      document.body.style.overflow = "";
      [t1, t2, t3, t4].forEach(clearTimeout);
    };
  }, []);

  const stacked = phase !== "join";

  return (
    <AnimatePresence>
      <motion.div
        data-testid="intro-overlay"
        onClick={finish}
        className="fixed inset-0 z-[100] bg-[#090A0C] flex items-center justify-center cursor-pointer"
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === "fly" ? 0 : 1 }}
        transition={{ duration: phase === "fly" ? 1.3 : 0.3, ease: "easeInOut" }}
      >
        <div
          className="absolute inset-0 blueprint-grid opacity-30"
          style={{ ["--grid-line" as string]: "rgba(59,130,246,0.07)" }}
        />

        <motion.div
          className="relative flex flex-col items-center"
          animate={
            phase === "fly"
              ? { x: target.x, y: target.y, scale: 0.16, opacity: 0 }
              : { x: 0, y: 0, scale: 1, opacity: 1 }
          }
          transition={{ duration: 1.3, ease: [0.55, 0.06, 0.35, 1] }}
        >
          <div className="relative" style={{ width: ICON_W, height: ICON_H }}>
            <motion.img
              src="/brand/logo-ring-left.png"
              alt=""
              className="absolute left-0 top-0 h-full"
              initial={{ x: -EDGE, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ x: { duration: 2.5, ease: [0.45, 0.05, 0.55, 0.95] }, opacity: { duration: 0.9 } }}
            />
            <motion.img
              src="/brand/logo-ring-right.png"
              alt=""
              className="absolute right-0 top-0 h-full"
              initial={{ x: EDGE, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ x: { duration: 2.5, ease: [0.45, 0.05, 0.55, 0.95] }, opacity: { duration: 0.9 } }}
            />
            {stacked &&
              [5, 4, 3, 2, 1, 0].map((lvl, seq) => (
                <motion.img
                  key={lvl}
                  src={`/brand/logo-chev-lvl${lvl}.png`}
                  alt=""
                  className="absolute left-0 w-full"
                  style={{ top: `${lvl * 16.6667}%`, height: "16.75%" }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: seq * 0.52, duration: 0.5, ease: "easeOut" }}
                />
              ))}
          </div>

          <motion.div
            className="mt-6 flex items-baseline gap-2.5"
            initial={{ opacity: 0, y: 14 }}
            animate={phase === "name" || phase === "fly" ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <span className="font-logo text-4xl md:text-5xl text-white">FOSCHE</span>
            <span className="font-logo text-sm text-[#6A7CB5]">LLC</span>
          </motion.div>
        </motion.div>

        <motion.p
          className="absolute bottom-8 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-white/25"
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === "fly" ? 0 : 1 }}
          transition={{ delay: 1, duration: 0.8 }}
        >
          Click to skip
        </motion.p>
      </motion.div>
    </AnimatePresence>
  );
};
